import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, DollarSign, TrendingUp, Landmark, ArrowDownToLine } from "lucide-react";

const wdStatusBadge = (status) => {
  const s = (status || "").toLowerCase();
  const cls = s === "paid" ? "bg-green-600" : s === "approved" ? "bg-blue-500" : s === "rejected" ? "bg-red-500" : "bg-amber-500";
  return <Badge className={`${cls} capitalize`}>{status}</Badge>;
};

export const WalletPayouts = ({ wallet, format, wdAmount, setWdAmount, onWithdraw, payout, setPayout, onSavePayout, currency = "EUR", rate = 1, currencySymbol = "€" }) => {
  const balance = wallet?.balance || 0;
  const threshold = wallet?.threshold || 0;
  const ledger = wallet?.ledger || [];
  const withdrawals = wallet?.withdrawals || [];
  const totalEarned = ledger.reduce((s, l) => s + (l.credit || 0), 0);
  const totalWithdrawn = withdrawals.filter((w) => (w.status || "").toLowerCase() === "paid").reduce((s, w) => s + (w.amount || 0), 0);
  const wdEur = (parseFloat(wdAmount) || 0) / (rate || 1); // input is in the seller's display currency
  const canWithdraw = wdEur >= threshold && wdEur <= balance;
  const payoutReady = payout.account_number && payout.bank_code && payout.account_name;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-1 bg-primary text-white" data-testid="wallet-balance-card">
          <Wallet className="w-5 h-5 mb-2 opacity-90" />
          <p className="text-3xl font-black font-display">{format(balance)}</p>
          <p className="text-xs opacity-90 mt-1">Available balance</p>
        </Card>
        <Card className="p-5"><TrendingUp className="w-5 h-5 text-green-600 mb-2" /><p className="text-2xl font-black font-display">{format(totalEarned)}</p><p className="text-xs text-muted-foreground">Lifetime earnings</p></Card>
        <Card className="p-5"><ArrowDownToLine className="w-5 h-5 text-secondary mb-2" /><p className="text-2xl font-black font-display">{format(totalWithdrawn)}</p><p className="text-xs text-muted-foreground">Total withdrawn</p></Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Card className="p-6">
          <h3 className="font-semibold text-lg flex items-center gap-2"><DollarSign className="w-5 h-5 text-primary" />Withdraw funds</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Minimum withdrawal is {format(threshold)}. Requests are reviewed by an admin.</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{currencySymbol}</span>
              <Input data-testid="withdraw-amount" type="number" placeholder="Amount" className="pl-7" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} />
            </div>
            <Button data-testid="withdraw-btn" onClick={onWithdraw} disabled={!canWithdraw || !payoutReady} className="rounded-full whitespace-nowrap">
              <DollarSign className="w-4 h-4 mr-1" />Withdraw
            </Button>
          </div>
          {currency !== "EUR" && wdAmount !== "" && <p className="text-[11px] text-muted-foreground mt-1" data-testid="withdraw-eur">≈ €{wdEur.toFixed(2)} will be deducted from your balance</p>}
          {!payoutReady && <p className="text-xs text-amber-600 mt-2">Add your payout details first to enable withdrawals.</p>}
        </Card>

        <Card className="p-6" data-testid="payout-details">
          <h3 className="font-semibold text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-primary" />Payout bank details</h3>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Approved withdrawals are transferred to this account (via Paystack).</p>
          <div className="space-y-3">
            <div><Label className="text-xs">Account holder name</Label><Input data-testid="payout-account-name" value={payout.account_name} onChange={(e) => setPayout({ ...payout, account_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Bank name</Label><Input data-testid="payout-bank-name" value={payout.bank_name} onChange={(e) => setPayout({ ...payout, bank_name: e.target.value })} /></div>
              <div><Label className="text-xs">Bank code</Label><Input data-testid="payout-bank-code" value={payout.bank_code} onChange={(e) => setPayout({ ...payout, bank_code: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Account number</Label><Input data-testid="payout-account-number" value={payout.account_number} onChange={(e) => setPayout({ ...payout, account_number: e.target.value })} /></div>
            <Button data-testid="save-payout-btn" onClick={onSavePayout} className="rounded-full">Save payout details</Button>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b"><h4 className="font-semibold text-sm">Earnings ledger</h4></div>
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Commission</TableHead><TableHead className="text-right">Credited</TableHead></TableRow></TableHeader>
            <TableBody>
              {ledger.slice(0, 12).map((l) => (
                <TableRow key={l.ledger_id} data-testid={`ledger-row-${l.ledger_id}`}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">-{format(l.commission || 0)}</TableCell>
                  <TableCell className="text-right text-sm font-medium text-green-600">+{format(l.credit)}</TableCell>
                </TableRow>
              ))}
              {ledger.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground text-sm">No earnings yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b"><h4 className="font-semibold text-sm">Withdrawal history</h4></div>
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead className="text-right">Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {withdrawals.map((w) => (
                <TableRow key={w.withdrawal_id} data-testid={`withdrawal-row-${w.withdrawal_id}`}>
                  <TableCell className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-sm font-medium">{format(w.amount)}</TableCell>
                  <TableCell className="text-right">{wdStatusBadge(w.status)}</TableCell>
                </TableRow>
              ))}
              {withdrawals.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground text-sm">No withdrawals yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
};
